import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Lightning } from '@sorare/core/src/atoms/icons/Lightning';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { CircularProgress } from '@sorare/core/src/atoms/loader/CircularProgress';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { LeaderboardRow } from '@sorare/core/src/components/layout/LeaderboardRow';
import { ClubShield } from '@sorare/core/src/components/user/ClubShield';
import { ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import type { CollectionLeaderboardRow_userCardCollection } from './__generated__/index.graphql';

const Children = styled(Vertical).attrs({ gap: 0.5 })`
  align-items: end;
`;
const StyledProgress = styled(CircularProgress)`
  width: var(--intermediate-unit);
  --circular-progress-stroke: var(--c-onBase-success);
`;

type Props = {
  userCardCollection: CollectionLeaderboardRow_userCardCollection;
  collectionSlug: string;
  highlighted?: boolean;
};

export const CollectionLeaderboardRow = ({
  userCardCollection,
  highlighted,
  collectionSlug,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { liveRanking, fulfilledSlotsCount, slotsCount, score, user } =
    userCardCollection;
  const { anyUserSportProfile } = user;

  return (
    <LeaderboardRow
      highlighted={highlighted}
      rank={liveRanking}
      user={user}
      to={generateSportContextPath(
        ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION,
        {
          params: {
            slug: userCardCollection.user.slug,
            collection: collectionSlug,
          },
        }
      )}
      caption={
        <Horizontal gap={0.5}>
          <ClubShield
            size={1.5}
            shieldUrl={anyUserSportProfile?.clubBadge?.pictureUrl}
            clubName={anyUserSportProfile?.clubName}
          />
          <LabelS color="var(--c-nd-600)">
            <Ellipsis>{anyUserSportProfile?.clubName}</Ellipsis>
          </LabelS>
        </Horizontal>
      }
    >
      <Children>
        <Horizontal>
          <LabelM bold>
            <FormattedNumber value={score} />
          </LabelM>
          <Lightning size={1.5} />
        </Horizontal>
        <Horizontal>
          <Horizontal gap={0}>
            <LabelS color="var(--c-nd-600)">
              <b>
                <FormattedNumber value={fulfilledSlotsCount} />
              </b>
              /<FormattedNumber value={slotsCount} />
            </LabelS>
          </Horizontal>
          <StyledProgress
            maxProgress={slotsCount}
            progress={fulfilledSlotsCount}
          />
        </Horizontal>
      </Children>
    </LeaderboardRow>
  );
};

CollectionLeaderboardRow.fragments = {
  userCardCollection: gql`
    fragment CollectionLeaderboardRow_userCardCollection on UserCardCollection {
      id
      liveRanking
      score
      fulfilledSlotsCount
      slotsCount
      user {
        slug
        anyUserSportProfile(sport: $sport) {
          id
          clubName
          clubBadge {
            id
            pictureUrl
          }
        }
        ...LeaderboardRow_publicUserInfoInterface
      }
    }
    ${LeaderboardRow.fragments.publicUserInfoInterface}
  ` as TypedDocumentNode<CollectionLeaderboardRow_userCardCollection>,
};
